import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
    DriverActivityAdminDetail,
    DriverActivityAdminStatus,
    DriverActivityRule,
    DriverActivityRuleInput,
    DriverActivityRuleTemplate,
    SaveDriverActivityInput,
    UpdateDriverActivityLimitedInput,
} from '../graphql/types';
import { RuleTextDraft, ruleTextFromList, ruleTextToInput } from './ruleTextUtils';

dayjs.extend(utc);
dayjs.extend(timezone);

export const NAME_MAX_LENGTH = 20;
export const MAX_RULES = 5;
export const MAX_LEVELS = 2;
export const ATTEND_DAYS_MIN = 1;
export const ATTEND_DAYS_MAX = 7;
export const OFFLINE_REASON_MAX_LENGTH = 256;
/** 活动最长 52 周 */
export const MAX_WEEKS = 52;
export const DATE_FORMAT = 'YYYY-MM-DD';
export const DEADLINE_FORMAT = 'YYYY-MM-DD HH:mm';
/** 活动时间口径：美西 */
export const ACTIVITY_TIME_ZONE = 'America/Los_Angeles';

/** 档位草稿：输入中允许为空 */
export interface RuleLevelDraft {
    dailyDeliverQty?: number | null;
    targetAttendDays?: number | null;
    rewardAmount?: number | null;
}

/** 规则草稿：一条规则 = 范围（Hub 多选 + WHS 多选）+ 1~2 个档位 */
export interface RuleDraft {
    /** 仅前端用的 React key */
    key: string;
    /** 编辑已有活动时后端的 scope 行 id；未开始/进行中只允许在该行上新增站点 */
    scopeId?: number | null;
    hubIds: number[];
    warehouseIds: number[];
    levels: RuleLevelDraft[];
}

export interface ActivityFormValues {
    name?: string;
    dateRange?: [Dayjs | null, Dayjs | null] | null;
    signUpDeadline?: Dayjs | null;
    bannerS3Key?: string;
    needSignUp: boolean;
    needRealName: boolean;
    maxRewardAmount?: number | null;
    rules: RuleDraft[];
    ruleText: RuleTextDraft;
}

let ruleKeySeq = 0;

export function createEmptyLevel(): RuleLevelDraft {
    return { dailyDeliverQty: null, targetAttendDays: null, rewardAmount: null };
}

export function createEmptyRule(): RuleDraft {
    ruleKeySeq += 1;
    return { key: `rule-${Date.now()}-${ruleKeySeq}`, scopeId: null, hubIds: [], warehouseIds: [], levels: [createEmptyLevel()] };
}

export function getDefaultFormValues(ruleText: RuleTextDraft): ActivityFormValues {
    return {
        name: '',
        dateRange: null,
        signUpDeadline: null,
        bannerS3Key: '',
        needSignUp: true,
        needRealName: true,
        maxRewardAmount: null,
        rules: [createEmptyRule()],
        ruleText,
    };
}

// ---------- 状态 ----------

/** 列表状态列：纯色加粗文字（原型 .st） */
export const STATUS_TEXT_COLOR: Record<DriverActivityAdminStatus, string> = {
    [DriverActivityAdminStatus.NOT_STARTED]: '#2F6FED',
    [DriverActivityAdminStatus.IN_PROGRESS]: '#3BA935',
    [DriverActivityAdminStatus.ENDED]: '#595959',
    [DriverActivityAdminStatus.OFFLINE]: '#F0453A',
    [DriverActivityAdminStatus.DRAFT]: '#8C8C8C',
};

/** 页头状态胶囊（原型 .tag / 页面 5 状态图配色） */
export const STATUS_TAG_STYLE: Record<DriverActivityAdminStatus, { background: string; color: string }> = {
    [DriverActivityAdminStatus.DRAFT]: { background: '#F2F2F2', color: '#595959' },
    [DriverActivityAdminStatus.NOT_STARTED]: { background: '#EEF4FF', color: '#2F6FED' },
    [DriverActivityAdminStatus.IN_PROGRESS]: { background: '#EAF7E8', color: '#3BA935' },
    [DriverActivityAdminStatus.ENDED]: { background: '#F5F5F5', color: '#595959' },
    [DriverActivityAdminStatus.OFFLINE]: { background: '#FFF1F0', color: '#F0453A' },
};

export function canEditActivity(status: DriverActivityAdminStatus): boolean {
    return (
        status === DriverActivityAdminStatus.DRAFT ||
        status === DriverActivityAdminStatus.NOT_STARTED ||
        status === DriverActivityAdminStatus.IN_PROGRESS
    );
}

/** 未开始 / 进行中：只能改文案、Banner、新增 Hub/WHS */
export function isLimitedEditStatus(status: DriverActivityAdminStatus): boolean {
    return status === DriverActivityAdminStatus.NOT_STARTED || status === DriverActivityAdminStatus.IN_PROGRESS;
}

export function canOnlineActivity(status: DriverActivityAdminStatus): boolean {
    return status === DriverActivityAdminStatus.DRAFT;
}

export function canOfflineActivity(status: DriverActivityAdminStatus): boolean {
    return status === DriverActivityAdminStatus.NOT_STARTED || status === DriverActivityAdminStatus.IN_PROGRESS;
}

export function canDeleteActivity(status: DriverActivityAdminStatus): boolean {
    return status === DriverActivityAdminStatus.DRAFT;
}

// ---------- 时间 ----------

export function isMonday(date: Dayjs): boolean {
    return date.day() === 1;
}

export function isSunday(date: Dayjs): boolean {
    return date.day() === 0;
}

/**
 * 活动时间区间控件的可选日期：选开始日只能点周一、选结束日只能点周日。
 * activeRange 取 RangePicker onFocus 给出的当前输入框（不能用 info.from 推断，先点结束框时会判反）；
 * from 是本次已选的另一端，用来保证开始 ≤ 结束，避免控件把顺序颠倒后变成「周日开始 / 周一结束」；
 * picked 是本次打开后已选的两端：两端都选好后控件会用本函数回校验两个已选日期（此时 activeRange 不代表被校验的那个框），
 * 已选的直接放行，否则开始日会被「结束只能周日」判掉、整段清空。
 */
export function isRangeDateDisabled(
    current: Dayjs,
    activeRange: 'start' | 'end',
    from?: Dayjs | null,
    picked?: [Dayjs | null, Dayjs | null],
): boolean {
    if (picked && picked[0] && picked[1] && (current.isSame(picked[0], 'day') || current.isSame(picked[1], 'day'))) {
        return false;
    }
    if (activeRange === 'end') {
        return !isSunday(current) || (!!from && current.isBefore(from, 'day'));
    }
    return !isMonday(current) || (!!from && current.isAfter(from, 'day'));
}

/**
 * 活动周数：开始周一到结束周日，按日历天数 / 7。
 * 入参是 yyyy-MM-dd 字符串或 Dayjs，只做日历差，不涉及时区。
 * 不满整周（非周一开始 / 非周日结束）返回 null。
 */
export function computeWeekCount(start?: Dayjs | string | null, end?: Dayjs | string | null): number | null {
    if (!start || !end) return null;
    const s = dayjs(start).startOf('day');
    const e = dayjs(end).startOf('day');
    if (!s.isValid() || !e.isValid() || e.isBefore(s)) return null;
    if (!isMonday(s) || !isSunday(e)) return null;
    const days = e.diff(s, 'day') + 1;
    return days % 7 === 0 ? days / 7 : null;
}

export type DateRangeError = 'required' | 'notMonday' | 'notSunday' | 'beforeStart' | 'tooLong';

export function validateDateRange(range?: [Dayjs | null, Dayjs | null] | null): DateRangeError | null {
    if (!range || !range[0] || !range[1]) return 'required';
    const [start, end] = range;
    if (!isMonday(start)) return 'notMonday';
    if (!isSunday(end)) return 'notSunday';
    if (end.startOf('day').isBefore(start.startOf('day'))) return 'beforeStart';
    const weeks = computeWeekCount(start, end);
    if (weeks !== null && weeks > MAX_WEEKS) return 'tooLong';
    return null;
}

export type SignUpDeadlineError = 'required' | 'afterStart' | 'afterEnd';

/** 需报名时截止必填，且截止日 ≤ 活动开始日 */
/**
 * 报名截止校验（和后端口径一致）：默认允许中途报名，截止须 ≤ 活动结束日 23:59:59（10-23 用户定）；
 * 后台配置明确关闭中途报名时收紧为 ≤ 活动开始时刻（开始日 00:00，活动一开始就不能报名）。
 */
export function validateSignUpDeadline(
    deadline: Dayjs | null | undefined,
    needSignUp: boolean,
    start: Dayjs | null | undefined,
    end?: Dayjs | null,
    allowDuringActivity = true
): SignUpDeadlineError | null {
    if (!needSignUp) return null;
    if (!deadline) return 'required';
    if (allowDuringActivity) {
        return end && deadline.isAfter(end.endOf('day')) ? 'afterEnd' : null;
    }
    return start && deadline.isAfter(start.startOf('day')) ? 'afterStart' : null;
}

/** 活动开始日是否就是美西的今天（默认配置下这种需报名活动上线会被报名截止拦住，表单提前提示） */
export function isStartDateTodayLa(start?: Dayjs | null, now: Dayjs = dayjs()): boolean {
    if (!start) return false;
    return start.format(DATE_FORMAT) === now.tz(ACTIVITY_TIME_ZONE).format(DATE_FORMAT);
}
/**
 * 后台所有时间展示统一美西：时间戳（epoch 毫秒）按 America/Los_Angeles 格式化。
 * 'full' = YYYY-MM-DD HH:mm，'short' = MM-DD HH:mm。
 */
export function formatLaDateTime(timestamp?: number | null, pattern: 'full' | 'short' = 'full'): string {
    if (timestamp === null || timestamp === undefined || !Number.isFinite(Number(timestamp))) return '--';
    const value = dayjs(Number(timestamp)).tz(ACTIVITY_TIME_ZONE);
    return value.format(pattern === 'full' ? 'YYYY-MM-DD HH:mm' : 'MM-DD HH:mm');
}

// ---------- 金额 ----------

export type MaxRewardError = 'required' | 'invalid' | 'belowWeeklyTotal';

/** 单人上限：必填、> 0、最多 2 位小数 */
/**
 * 传了规则和周数时还要 ≥ 最高档金额 × 周数：活动级累计上限，低于它司机周周达标也拿不满（10-22 用户定由提示改为拦截，后端同口径）。
 */
export function validateMaxReward(value?: number | null, rules?: RuleDraft[], weekCount?: number | null): MaxRewardError | null {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return 'required';
    if (!isValidAmount(value)) return 'invalid';
    if (rules && isMaxRewardSuspicious(rules, weekCount ?? null, value)) return 'belowWeeklyTotal';
    return null;
}

export function isValidAmount(value: number): boolean {
    if (!Number.isFinite(value) || value <= 0) return false;
    return /^\d+(\.\d{1,2})?$/.test(String(value));
}

function isPositiveInt(value: number | null | undefined): value is number {
    return value !== null && value !== undefined && Number.isInteger(value) && value > 0;
}

/** 金额用 String 走契约；保留用户输入的小数位，不足补到两位 */
export function amountToString(value: number): string {
    return Number(value).toFixed(2);
}

/** 最高档金额 × 周数 > 上限（validateMaxReward 用它拦截；周数未知时不判） */
export function isMaxRewardSuspicious(rules: RuleDraft[], weekCount: number | null, maxReward?: number | null): boolean {
    if (!weekCount || maxReward === null || maxReward === undefined) return false;
    let highest = 0;
    rules.forEach((rule) => {
        rule.levels.forEach((level) => {
            const amount = Number(level.rewardAmount);
            if (Number.isFinite(amount) && amount > highest) highest = amount;
        });
    });
    return highest > 0 && highest * weekCount > Number(maxReward);
}

// ---------- 范围冲突 ----------

export interface ScopeLike {
    hubIds: number[];
    warehouseIds: number[];
}

export interface ScopeConflict {
    /** 0 基规则下标 */
    ruleIndex: number;
    /** 规则间冲突时为对方下标；规则内冲突（选了 Hub 又选了它下面的 WHS）为 null */
    otherRuleIndex: number | null;
    /** 牵涉到的站点 id（Hub 或 WHS），用于把 chip 标红 */
    siteIds: number[];
}

function intersect(a: number[], b: number[]): number[] {
    const set = new Set(b);
    return a.filter((x) => set.has(x));
}

/**
 * 范围冲突：Hub > WHS。
 * 规则内：WHS 的所属 Hub 已被同一规则整体选中；
 * 规则间：hub 交集非空 ∥ 仓交集非空 ∥ 一方的仓所属 Hub 落在另一方的 hub 集合里。
 * 与后端 §10.5 同一套定义。whsHubMap: warehouseId -> hubId。
 */
export function findScopeConflicts(rules: ScopeLike[], whsHubMap: Record<number, number>): ScopeConflict[] {
    const conflicts: ScopeConflict[] = [];
    rules.forEach((rule, i) => {
        const covered = rule.warehouseIds.filter((w) => rule.hubIds.includes(whsHubMap[w]));
        if (covered.length) {
            const hubs = Array.from(new Set(covered.map((w) => whsHubMap[w])));
            conflicts.push({ ruleIndex: i, otherRuleIndex: null, siteIds: [...covered, ...hubs] });
        }
    });
    for (let i = 0; i < rules.length; i += 1) {
        for (let j = i + 1; j < rules.length; j += 1) {
            const a = rules[i];
            const b = rules[j];
            const siteIds = new Set<number>();
            intersect(a.hubIds, b.hubIds).forEach((h) => siteIds.add(h));
            intersect(a.warehouseIds, b.warehouseIds).forEach((w) => siteIds.add(w));
            a.warehouseIds.forEach((w) => {
                if (b.hubIds.includes(whsHubMap[w])) {
                    siteIds.add(w);
                    siteIds.add(whsHubMap[w]);
                }
            });
            b.warehouseIds.forEach((w) => {
                if (a.hubIds.includes(whsHubMap[w])) {
                    siteIds.add(w);
                    siteIds.add(whsHubMap[w]);
                }
            });
            if (siteIds.size) {
                conflicts.push({ ruleIndex: i, otherRuleIndex: j, siteIds: Array.from(siteIds) });
            }
        }
    }
    return conflicts;
}

// ---------- 规则校验 ----------

export type RuleErrorReason =
    | 'required'
    | 'tooManyRules'
    | 'scopeRequired'
    | 'levelOneRequired'
    | 'dailyQtyInvalid'
    | 'attendDaysInvalid'
    | 'rewardInvalid'
    | 'levelTwoDaysNotGreater'
    | 'levelTwoRewardNotGreater';

export interface RuleValidationError {
    /** 1 基规则序号；整体性错误（required / tooManyRules）为 0 */
    ruleNo: number;
    /** 1 基档位序号；非档位错误为 0 */
    level: number;
    reason: RuleErrorReason;
}

export interface RuleValidationResult {
    errors: RuleValidationError[];
    conflicts: ScopeConflict[];
}

/** 规则 ≥1 条 ≤5 条、范围非空且 ≤5、档位 1 完整合法、档位 2 天数和金额 > 档位 1、范围不冲突 */
export function validateRules(rules: RuleDraft[], whsHubMap: Record<number, number>): RuleValidationResult {
    const errors: RuleValidationError[] = [];
    if (!rules.length) {
        return { errors: [{ ruleNo: 0, level: 0, reason: 'required' }], conflicts: [] };
    }
    if (rules.length > MAX_RULES) {
        errors.push({ ruleNo: 0, level: 0, reason: 'tooManyRules' });
    }
    rules.forEach((rule, index) => {
        const ruleNo = index + 1;
        const siteCount = rule.hubIds.length + rule.warehouseIds.length;
        if (siteCount === 0) errors.push({ ruleNo, level: 0, reason: 'scopeRequired' });

        const levelOne = rule.levels[0];
        if (!levelOne) {
            errors.push({ ruleNo, level: 1, reason: 'levelOneRequired' });
            return;
        }
        rule.levels.slice(0, MAX_LEVELS).forEach((level, levelIndex) => {
            const levelNo = levelIndex + 1;
            const missing =
                level.dailyDeliverQty === null || level.dailyDeliverQty === undefined ||
                level.targetAttendDays === null || level.targetAttendDays === undefined ||
                level.rewardAmount === null || level.rewardAmount === undefined;
            if (missing) {
                errors.push({ ruleNo, level: levelNo, reason: 'levelOneRequired' });
                return;
            }
            if (!isPositiveInt(level.dailyDeliverQty)) errors.push({ ruleNo, level: levelNo, reason: 'dailyQtyInvalid' });
            if (
                !isPositiveInt(level.targetAttendDays) ||
                level.targetAttendDays < ATTEND_DAYS_MIN ||
                level.targetAttendDays > ATTEND_DAYS_MAX
            ) {
                errors.push({ ruleNo, level: levelNo, reason: 'attendDaysInvalid' });
            }
            if (!isValidAmount(Number(level.rewardAmount))) errors.push({ ruleNo, level: levelNo, reason: 'rewardInvalid' });
        });
        const levelTwo = rule.levels[1];
        if (levelTwo && levelOne) {
            if (
                levelTwo.targetAttendDays !== null && levelTwo.targetAttendDays !== undefined &&
                levelOne.targetAttendDays !== null && levelOne.targetAttendDays !== undefined &&
                levelTwo.targetAttendDays <= levelOne.targetAttendDays
            ) {
                errors.push({ ruleNo, level: 2, reason: 'levelTwoDaysNotGreater' });
            }
            if (
                levelTwo.rewardAmount !== null && levelTwo.rewardAmount !== undefined &&
                levelOne.rewardAmount !== null && levelOne.rewardAmount !== undefined &&
                Number(levelTwo.rewardAmount) <= Number(levelOne.rewardAmount)
            ) {
                errors.push({ ruleNo, level: 2, reason: 'levelTwoRewardNotGreater' });
            }
        }
    });
    return { errors, conflicts: findScopeConflicts(rules, whsHubMap) };
}

export type NameError = 'required' | 'tooLong';

export function validateName(name?: string | null): NameError | null {
    const trimmed = (name ?? '').trim();
    if (!trimmed) return 'required';
    if (trimmed.length > NAME_MAX_LENGTH) return 'tooLong';
    return null;
}

// ---------- 表单 ↔ 契约 ----------

/** 档位 2 的妥投门槛必须等于档位 1：序列化时统一取档位 1 的值，两行存同值 */
/** 没填的数字传 null（预览渲染成占位、保存/上线由后端按必填拦），不要偷偷当 0 */
function numberOrNull(value: number | null | undefined): number | null {
    return value === null || value === undefined || Number.isNaN(Number(value)) ? null : Number(value);
}

export function rulesToInput(rules: RuleDraft[]): DriverActivityRuleInput[] {
    return rules.map((rule) => {
        const dailyQty = numberOrNull(rule.levels[0]?.dailyDeliverQty);
        return {
            scopeId: rule.scopeId ?? null,
            hubIds: rule.hubIds,
            warehouseIds: rule.warehouseIds,
            levels: rule.levels.slice(0, MAX_LEVELS).map((level, index) => {
                const reward = numberOrNull(level.rewardAmount);
                return {
                    level: index + 1,
                    dailyDeliverQty: dailyQty,
                    targetAttendDays: numberOrNull(level.targetAttendDays),
                    rewardAmount: reward === null ? null : amountToString(reward),
                };
            }),
        };
    });
}

export function rulesFromDetail(rules: DriverActivityRule[] | undefined | null): RuleDraft[] {
    if (!rules?.length) return [createEmptyRule()];
    return rules.map((rule) => {
        const draft = createEmptyRule();
        return {
            ...draft,
            scopeId: rule.scopeId ?? null,
            hubIds: (rule.hubIds ?? []).map(Number),
            warehouseIds: (rule.warehouseIds ?? []).map(Number),
            levels: [...(rule.levels ?? [])]
                .sort((a, b) => a.level - b.level)
                .map((level) => ({
                    dailyDeliverQty: level.dailyDeliverQty,
                    targetAttendDays: level.targetAttendDays,
                    rewardAmount: Number(level.rewardAmount),
                })),
        };
    });
}

export function buildSaveInput(
    values: ActivityFormValues,
    template: DriverActivityRuleTemplate,
    id?: number | null,
    ignoreTranslationWarning?: boolean
): SaveDriverActivityInput {
    const [start, end] = values.dateRange ?? [null, null];
    return {
        id: id ?? null,
        name: (values.name ?? '').trim(),
        startDate: start ? start.format(DATE_FORMAT) : '',
        endDate: end ? end.format(DATE_FORMAT) : '',
        signUpDeadline: values.needSignUp && values.signUpDeadline ? values.signUpDeadline.format(DEADLINE_FORMAT) : null,
        bannerS3Key: values.bannerS3Key ?? '',
        needSignUp: values.needSignUp,
        needRealName: values.needRealName,
        maxRewardAmount: amountToString(Number(values.maxRewardAmount ?? 0)),
        rules: rulesToInput(values.rules),
        ruleText: ruleTextToInput(values.ruleText, template.languages, template.sections),
        ignoreTranslationWarning: ignoreTranslationWarning ?? null,
    };
}

export function buildLimitedInput(
    values: ActivityFormValues,
    template: DriverActivityRuleTemplate,
    id: number,
    ignoreTranslationWarning?: boolean
): UpdateDriverActivityLimitedInput {
    return {
        id,
        bannerS3Key: values.bannerS3Key ?? null,
        ruleText: ruleTextToInput(values.ruleText, template.languages, template.sections),
        rules: rulesToInput(values.rules),
        ignoreTranslationWarning: ignoreTranslationWarning ?? null,
    };
}

/** 详情 → 编辑表单初值；列表「上线」也借它把详情转成 SaveDriverActivityInput */
export function detailToFormValues(detail: DriverActivityAdminDetail, template: DriverActivityRuleTemplate): ActivityFormValues {
    const start = detail.startDate ? dayjs(detail.startDate, DATE_FORMAT) : null;
    const end = detail.endDate ? dayjs(detail.endDate, DATE_FORMAT) : null;
    return {
        name: detail.name,
        dateRange: start && end ? [start, end] : null,
        signUpDeadline: detail.signUpDeadline ? dayjs(detail.signUpDeadline, DEADLINE_FORMAT) : null,
        bannerS3Key: detail.bannerS3Key ?? '',
        needSignUp: detail.needSignUp,
        needRealName: detail.needRealName,
        maxRewardAmount: detail.maxRewardAmount !== null && detail.maxRewardAmount !== undefined ? Number(detail.maxRewardAmount) : null,
        rules: rulesFromDetail(detail.rules),
        ruleText: ruleTextFromList(detail.ruleText, template.languages, template.sections),
    };
}

/** 未开始/进行中「只能新增不能删」：新集合必须是旧集合的超集，否则把被删掉的补回去 */
export function keepOriginalSites(next: number[], original: number[]): number[] {
    const set = new Set(next);
    const restored = [...next];
    original.forEach((id) => {
        if (!set.has(id)) restored.push(id);
    });
    return restored;
}
